import React, { useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import * as DetailsIcons from "@common/components/DetailsPane/icons";
import Icon from "@common/components/Icon/Icon";
import { SubNavigation } from "@common/components/Navigation/SubNavigation/SubNavigation.styles";
import PageContent from "@common/components/PageContent/PageContent";
import { ScrollContainer } from "@common/components/ScrollContainer/ScrollContainer";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { PlatformName } from "@common/models";
import { ReleaseHealthOverview } from "@common/models/Analytics.model";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import useReleasesConsumer from "@main/pages/Release/consumers/useReleasesConsumer";
import { ReleaseMetricScore } from "@main/pages/Release/views/MetricCards/ReleaseMetricCard";
import { ReleaseVersionDropdown } from "@main/pages/Release/views/ReleaseVersionDropdown";
import * as NavStyled from "@main/pages/UserFeedback/views/UserFeedback.styles";
import { MetricKeys, RoutePaths } from "@main/router/config/routePaths";
import {
  generatePath,
  matchPath,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

const BACK_CONTAINER_ID = "metric-details-back-nav";

export const METRIC_TABS: Array<{ name: string; path: RoutePaths; metrics: string[] }> = [
  {
    name: "Data",
    path: RoutePaths.RELEASE_METRIC_DATA,
    metrics: [MetricKeys.networkSpeed, MetricKeys.endpointHealth, MetricKeys.testingCoverage],
  },
  {
    name: "Score",
    path: RoutePaths.RELEASE_METRIC_SCORE,
    metrics: Object.values(MetricKeys),
  },
  {
    name: "Guardrails",
    path: RoutePaths.RELEASE_METRIC_GUARDRAILS,
    metrics: [],
  },
  {
    name: "About",
    path: RoutePaths.RELEASE_METRIC_ABOUT,
    metrics: Object.values(MetricKeys),
  },
  {
    name: "Metadata dimensions",
    path: RoutePaths.RELEASE_METRIC_METADATA_DIMENSIONS,
    metrics: [MetricKeys.testingCoverage],
  },
];

const METRIC_DISPLAY_NAMES: Record<string, string> = {
  [MetricKeys.releaseHealth]: "Release health",
  [MetricKeys.crashFreeSessions]: "Crash-free sessions",
  [MetricKeys.appHangFreeSessions]: "App hang-free sessions",
  [MetricKeys.crashFreeSessions]: "App hang-free sessions",
  [MetricKeys.resolutionRate]: "Resolution rate",
  [MetricKeys.endpointHealth]: "Endpoints health",
  [MetricKeys.networkSpeed]: "Network traffic speed",
  [MetricKeys.testingCoverage]: "Coverage",
  [MetricKeys.currentDuration]: "Current duration",
  [MetricKeys.releaseDateEstimate]: "Release date estimate",
  [MetricKeys.mttr]: "MTTR",
};

export type MetricScoreOutletContext = { savedScore: number | undefined | null; refetchReleaseStatus: () => void };

export default function ReleaseMetricDetailsLayout() {
  const navigate = useNavigate();
  const { sendEvent } = useAnalytics();

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { metricKey } = useParams<{ metricKey: string }>();
  const location = useLocation();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Release metric details", metric_key: metricKey });
  }, [sendEvent, metricKey]);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlVersion = searchParams.get("version");

  const {
    healthData,
    releaseVersions,
    releaseSearch,
    setReleaseSearch,
    releasesLoading,
    hasMore,
    isFetchingNextPage,
    loadNext,
    selectedVersion,
    setSelectedVersion,
    refetchReleaseStatus,
  } = useReleasesConsumer(urlVersion);

  const versionSearch = selectedVersion ? `?version=${encodeURIComponent(selectedVersion)}` : "";

  const metricScore = getMetricScore(metricKey, healthData);

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version);
    setSearchParams({ version });
  };

  // The consumer resolves selectedVersion (preferring the URL param when it's a real version,
  // falling back to the latest release otherwise) — mirror that back into the URL so it stays
  // the single source of truth / a shareable link.
  useEffect(() => {
    if (!selectedVersion || searchParams.get("version") === selectedVersion) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("version", selectedVersion);
    setSearchParams(nextParams, { replace: true });
  }, [selectedVersion, searchParams, setSearchParams]);

  const versionPickerElement = (
    <ReleaseVersionDropdown
      releaseVersions={releaseVersions}
      selectedVersion={selectedVersion}
      searchValue={releaseSearch}
      onVersionChange={handleVersionChange}
      onSearchChange={setReleaseSearch}
      loading={releasesLoading}
      hasMore={hasMore}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={loadNext}
    />
  );

  const backButtonElement = (
    <NavigationCustomElement>
      <NavStyled.NavigationTitle
        as="div"
        $isButton={false}
      >
        <div>
          <Tooltip
            style={{ width: "auto" }}
            text="Back to Releases"
            position="bottom"
          >
            <NavStyled.NavbarNavigation
              onClick={() =>
                navigate(
                  `${generatePath(RoutePaths.RELEASES, {
                    workspaceSlug: selectedWorkspace?.slug ?? null,
                    appKey: selectedApp?.key ?? null,
                  })}${versionSearch}`,
                )
              }
            >
              <DetailsIcons.SendIcon
                height={20}
                width={20}
              />
            </NavStyled.NavbarNavigation>
          </Tooltip>
          <p>{getMetricName(metricKey ?? "", selectedApp?.platform.name ?? PlatformName.IOS)}</p>
        </div>
      </NavStyled.NavigationTitle>
    </NavigationCustomElement>
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: versionPickerElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: backButtonElement,
    containerId: BACK_CONTAINER_ID,
    tagName: "li",
  });

  return (
    <>
      <Flex style={{ flex: 1, paddingLeft: "2rem", height: "100%" }}>
        <SubNavigation>
          <ul>
            {METRIC_TABS.map((tab) => {
              if (!tab.metrics.includes(metricKey ?? "")) return null;

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const to = generatePath(tab.path, {
                workspaceSlug: selectedWorkspace?.slug ?? null,
                appKey: selectedApp?.key ?? null,
                metricKey: metricKey ?? null,
              });
              const isActive = !!matchPath(tab.path, location.pathname);
              const isScoreTab = tab.path === RoutePaths.RELEASE_METRIC_SCORE;
              return (
                <li
                  key={tab.name}
                  className={isActive ? "active" : ""}
                >
                  <NavLink
                    to={{ pathname: to, search: versionSearch }}
                    onClick={() => sendEvent("release_metric_tab_selected", { metric_key: metricKey, tab: tab.name })}
                    style={isScoreTab ? { display: "flex", alignItems: "center" } : undefined}
                  >
                    {tab.name}
                    {isScoreTab && metricScore !== undefined && (
                      <ReleaseMetricScore
                        score={metricScore}
                        style={{ marginLeft: "auto", marginRight: "3.2rem" }}
                      />
                    )}
                    <Icon
                      icon="caret-right"
                      size={10}
                    />
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </SubNavigation>
        <ScrollContainer>
          <PageContent width={"70rem"}>
            <Outlet context={{ savedScore: metricScore, refetchReleaseStatus } satisfies MetricScoreOutletContext} />
          </PageContent>
        </ScrollContainer>
      </Flex>
    </>
  );
}

const getMetricScore = (
  metricKey: string | undefined,
  healthData: ReleaseHealthOverview | undefined,
): number | undefined | null => {
  if (!metricKey || !healthData) return undefined;
  switch (metricKey) {
    case MetricKeys.releaseHealth:
      return healthData.release_health_score;
    case MetricKeys.crashFreeSessions:
      return healthData.crash_free_sessions.score;
    case MetricKeys.resolutionRate:
      return healthData.resolution_rate.score;
    case MetricKeys.mttr:
      return healthData.mttr.score;
    case MetricKeys.testingCoverage:
      return healthData.testing_coverage.score;
    case MetricKeys.networkSpeed:
      return healthData.network_speed.score;
    case MetricKeys.endpointHealth:
      return healthData.network_health.score;
    default:
      return undefined;
  }
};

const getMetricName = (metricKey: string, platform: PlatformName) => {
  if (metricKey === MetricKeys.appHangFreeSessions && platform === PlatformName.ANDROID) return "ANR-free sessions";
  return metricKey ? METRIC_DISPLAY_NAMES[metricKey] : "";
};
